import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { getComponentForEnumValue } from "./add-card/index.js";
import { callAPI, urlSearchParamsToObject } from "./utils";
import {
  decodePaymentData,
  getPaymentDataFromUrl,
  hasCompleteCreditCorpData,
  hasCompleteAvisData,
  hasCompletePayMayaData,
} from "./paymentData.utils.js";
import { getPaymentErrorMessage } from "./paymentErrorMessages.js";
import {
  getPaymentRedirectUrl,
  shouldUseHostedRedirect,
} from "./paymentRedirect.utils.js";

function AddingCard() {
  const [alertMessage, setAlertMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  let [params] = useSearchParams();
  const data = params.get("data");
  const { token } = useParams();
  const { gateway } = useParams();
  const [componentInfo, setComponentInfo] = useState({
    component: null,
    props: {},
  });

  useEffect(() => {
    setIsLoading(true);
    getPaymentGateWay(data ?? token, gateway);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, token, gateway]);

  const alert = (message) => {
    setAlertMessage(message);
  };

  const handleCloseAlert = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMessage(null);
  };

  const getPaymentGateWay = async (token, gateway) => {
    try {
      var searchParams = decodePaymentData(token);
      var params = urlSearchParamsToObject(searchParams, null);
      const fleetToken = params.f;
      var userId = params.i;
      var userType = params.t;
      var returnUrl = params.u;
      var returnUrlFailed = params.r;
      const payloadGateway = params.gateway ?? gateway;
      const hasCompletePaymentData =
        hasCompletePayMayaData(params, payloadGateway) ||
        hasCompleteCreditCorpData(params, payloadGateway) ||
        hasCompleteAvisData(params, payloadGateway);
      if (fleetToken && !hasCompletePaymentData) {
        const data = await callAPI(
          `${process.env.REACT_APP_WEBHOOK_API_ENDPOINT}/api/webhook/genericCreditForm`,
          "POST",
          {
            fleetToken,
            userId,
            userType,
            returnUrl,
            returnUrlFailed,
            gateway: payloadGateway,
            orderId: params.tranId,
            type: params.type,
          }
        );
        if (!data?.response) {
          throw new Error(
            getPaymentErrorMessage(
              data,
              `Unable to load payment form (${data?.returnCode ?? "unknown"})`
            )
          );
        }
        const paymentUrl = getPaymentRedirectUrl(
          data.response,
          payloadGateway
        );
        if (shouldUseHostedRedirect(data.response, payloadGateway)) {
          if (paymentUrl) {
            window.location.href = paymentUrl;
          } else {
            setAlertMessage("Something went wrong, no redirect URL!");
            setIsLoading(false);
          }
        } else {
          if (!paymentUrl) {
            throw new Error("Unable to load payment form: URL is missing.");
          }
          searchParams = getPaymentDataFromUrl(paymentUrl);
          params = { ...params, ...data.response };
          params = urlSearchParamsToObject(searchParams, params);
        }
      }
      userId = params.i ?? params.userId ?? userId;
      userType = params.t ?? params.userType ?? userType;
      returnUrl = params.u ?? params.returnUrl ?? returnUrl;
      returnUrlFailed = params.r ?? params.returnUrlFailed ?? returnUrlFailed;
      gateway = params.gateway ?? gateway;
      if (gateway) {
        let component = getComponentForEnumValue(gateway);
        if (component) {
          setComponentInfo({
            component,
            props: {
              ...params,
              ...{
                fleetToken,
                userId,
                userType,
                returnUrl,
                returnUrlFailed,
                searchParams,
              },
            },
          });
          setIsLoading(false);
        }
      }
    } catch (error) {
      setAlertMessage(
        [error?.method, error?.message].filter(Boolean).join(" ") ||
          "Unable to load payment form"
      );
      setIsLoading(false);
    }
  };

  const handlePaymentSuccess = (response) => {
    // Send payment success result to parent window
    if (window.parent !== window) {
      window.parent.postMessage({
        type: 'CARD_ADDED',
        data: response
      }, '*');
    }
  };

  const handlePaymentFailure = (error) => {
    // Send payment failure to parent window
    if (window.parent !== window) {
      window.parent.postMessage({
        type: 'CARD_FAILED',
        error: error
      }, '*');
    }
  };

  const { component: Component, props } = componentInfo;
  return (
    <div>
      {Component ? (
        <Component 
          {...props} 
          alert={alert} 
          setIsLoading={setIsLoading}
          onSuccess={handlePaymentSuccess}
          onFailure={handlePaymentFailure} 
        />
      ) : null}
      <Snackbar
        open={!!alertMessage}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        xl={{
          position: "fixed",
          top: 10,
          left: 10,
        }}
      >
        <Alert onClose={handleCloseAlert} severity="error" variant="filled">
          {alertMessage}
        </Alert>
      </Snackbar>

      {isLoading && (
        <div id="loading">
          <CircularProgress />
        </div>
      )}
    </div>
  );
}

export default AddingCard;
